import React, { useState, useEffect } from 'react';
import { Card, Form, Row, Col, Table, Nav } from 'react-bootstrap';
import Content from "../../../Components/Dashboard/Content/Content";
import { usePrices } from "../../../context/PriceContext";

export default function Calculation() {
    const [activeTab, setActiveTab] = useState('futures');
    const prices = usePrices();

    // Get list of available services
    const availableServices = Object.keys(prices || {});

    // Futures State
    const [futuresData, setFuturesData] = useState({
        service: availableServices[0] || '',
        buyPrice: '',
        sellPrice: '',
        quantity: ''
    });

    // Options State
    const [optionsData, setOptionsData] = useState({
        service: availableServices[0] || '',
        buyPrice: '',
        sellPrice: '',
        quantity: '',
        lotSize: 1
    });

    // Update entry price when service changes
    useEffect(() => {
        if (futuresData?.service && prices[futuresData.service]) {
            setFuturesData(prev => ({
                ...prev,
                buyPrice: prices[futuresData.service]?.spot_price || ''
            }));
        }
    }, [futuresData.service, prices]);

    useEffect(() => {
        if (optionsData.service && prices[optionsData.service]) {
            setOptionsData(prev => ({
                ...prev,
                buyPrice: prices[optionsData.service].spot_price || ''
            }));
        }
    }, [optionsData.service, prices]);

    const handlePositiveNumber = (value, maxDecimals = null) => {
        if (value === '') return '';

        // minus block
        if (Number(value) < 0) return '';

        if (maxDecimals !== null && value.includes('.')) {
            const [int, dec] = value.split('.');
            return int + '.' + dec.slice(0, maxDecimals);
        }

        return value;
    };


    // Brokerage Configuration
    const BROKERAGE_RATE = 0.05; // 0.05% total brokerage
    const EXCHANGE_TXN_RATE = 0.002; // 0.002% exchange transaction charge

    // Calculate Futures P&L
    const calculateFuturesMetrics = () => {
        const { buyPrice, sellPrice, quantity } = futuresData;

        if (!buyPrice || !sellPrice || !quantity) {
            return {
                grossPnL: 0,
                totalBrokerage: 0,
                exchangeTxn: 0,
                totalCharges: 0,
                netPnL: 0,
                buyValue: 0,
                sellValue: 0,
                turnover: 0
            };
        }

        const buy = parseFloat(buyPrice);
        const sell = parseFloat(sellPrice);
        const qty = parseFloat(quantity);

        // Calculate gross P&L
        const grossPnL = (sell - buy) * qty;

        // Calculate buy and sell values
        const buyValue = buy * qty;
        const sellValue = sell * qty;

        // Calculate turnover (buy + sell)
        const turnover = buyValue + sellValue;

        // Calculate total brokerage (0.05% on turnover)
        const totalBrokerage = (turnover * BROKERAGE_RATE) / 100;

        // Calculate exchange transaction charges
        const exchangeTxn = (turnover * EXCHANGE_TXN_RATE) / 100;

        // Total charges
        const totalCharges = totalBrokerage + exchangeTxn;

        // Net P&L
        const netPnL = grossPnL - totalCharges;

        return {
            grossPnL: grossPnL.toFixed(2),
            totalBrokerage: totalBrokerage.toFixed(2),
            exchangeTxn: exchangeTxn.toFixed(2),
            totalCharges: totalCharges.toFixed(2),
            netPnL: netPnL.toFixed(2),
            buyValue: buyValue.toFixed(2),
            sellValue: sellValue.toFixed(2),
            turnover: turnover.toFixed(2)
        };
    };

    // Calculate Options P&L
    const calculateOptionsMetrics = () => {
        const { buyPrice, sellPrice, quantity, lotSize } = optionsData;

        if (!buyPrice || !sellPrice || !quantity) {
            return {
                grossPnL: 0,
                totalBrokerage: 0,
                exchangeTxn: 0,
                totalCharges: 0,
                netPnL: 0,
                buyValue: 0,
                sellValue: 0,
                totalLots: 0,
                turnover: 0
            };
        }

        const buy = parseFloat(buyPrice);
        const sell = parseFloat(sellPrice);
        const qty = parseFloat(quantity);
        const lot = parseFloat(lotSize);

        // Calculate total quantity (lots * lot size)
        const totalQty = qty * lot;

        // Calculate gross P&L
        const grossPnL = (sell - buy) * totalQty;

        // Calculate buy and sell values
        const buyValue = buy * totalQty;
        const sellValue = sell * totalQty;

        // Calculate turnover (buy + sell)
        const turnover = buyValue + sellValue;

        // Calculate total brokerage (0.05% on turnover)
        const totalBrokerage = (turnover * BROKERAGE_RATE) / 100;

        // Calculate exchange transaction charges
        const exchangeTxn = (turnover * EXCHANGE_TXN_RATE) / 100;

        // Total charges
        const totalCharges = totalBrokerage + exchangeTxn;

        // Net P&L
        const netPnL = grossPnL - totalCharges;

        return {
            grossPnL: grossPnL.toFixed(2),
            totalBrokerage: totalBrokerage.toFixed(2),
            exchangeTxn: exchangeTxn.toFixed(2),
            totalCharges: totalCharges.toFixed(2),
            netPnL: netPnL.toFixed(2),
            buyValue: buyValue.toFixed(2),
            sellValue: sellValue.toFixed(2),
            totalLots: qty,
            turnover: turnover.toFixed(2)
        };
    };

    const futuresMetrics = calculateFuturesMetrics();
    const optionsMetrics = calculateOptionsMetrics();
    const currentMetrics = activeTab === 'futures' ? futuresMetrics : optionsMetrics;

    // Get currency symbol based on service
    const getCurrencySymbol = (service) => {
        if (!service) return '$';
        if (service.includes('INR')) return '₹';
        return '$';
    };

    const currentCurrency = activeTab === 'futures'
        ? getCurrencySymbol(futuresData.service)
        : getCurrencySymbol(optionsData.service);

    return (
        <Content
            button_status={false}
            Page_title="Crypto P&L Calculator"
            Page_title_showClient="Calculate your profit/loss with brokerage charges"
        >
            <div className="calculation-container">
                {/* Tab Navigation */}
                <Card className="mb-4 shadow-sm">
                    <Card.Body className="p-3">
                        <Nav variant="pills" className="justify-content-center">
                            <Nav.Item>
                                <Nav.Link
                                    active={activeTab === 'futures'}
                                    onClick={() => setActiveTab('futures')}
                                    className="px-5 py-2 fw-semibold"
                                >
                                    Futures
                                </Nav.Link>
                            </Nav.Item>
                            <Nav.Item>
                                <Nav.Link
                                    active={activeTab === 'options'}
                                    onClick={() => setActiveTab('options')}
                                    className="px-5 py-2 fw-semibold"
                                >
                                    Options
                                </Nav.Link>
                            </Nav.Item>
                        </Nav>
                    </Card.Body>
                </Card>

                <Row className="g-4">
                    {/* Input Card */}
                    <Col lg={6}>
                        <Card className="shadow-sm h-100">
                            <Card.Header className="bg-primary text-white">
                                <h5 className="mb-0">
                                    {activeTab === 'futures' ? ' Futures Calculator' : ' Options Calculator'}
                                </h5>
                            </Card.Header>
                            <Card.Body className="p-4">
                                {activeTab === 'futures' ? (
                                    // Futures Form
                                    <>
                                        <Form.Group className="mb-3">
                                            <Form.Label className="fw-semibold">Select Service</Form.Label>
                                            <Form.Select
                                                value={futuresData.service}
                                                onChange={(e) => setFuturesData({ ...futuresData, service: e.target.value })}
                                                className="py-2"
                                            >
                                                <option value="">-- Select a service --</option>
                                                {availableServices.map(service => (
                                                    <option key={service} value={service}>
                                                        {service}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>

                                        <Form.Group className="mb-3">
                                            <Form.Label className="fw-semibold">
                                                Entry Price
                                                <span className="badge bg-primary ms-2">
                                                    Live: {getCurrencySymbol(futuresData.service)}
                                                    {prices[futuresData.service]?.spot_price
                                                        ? Number(prices[futuresData.service].spot_price).toFixed(3)
                                                        : '--'}
                                                </span>

                                            </Form.Label>
                                            <Form.Control
                                                type="number"
                                                min="0"
                                                step="0.001"
                                                value={futuresData.buyPrice}
                                                onChange={(e) =>
                                                    setFuturesData({
                                                        ...futuresData,
                                                        buyPrice: handlePositiveNumber(e.target.value, 3)
                                                    })
                                                }
                                                disabled={!futuresData.service}
                                            />

                                        </Form.Group>

                                        <Form.Group className="mb-3">
                                            <Form.Label className="fw-semibold">Exit Price</Form.Label>
                                            <Form.Control
                                                type="number"
                                                min="0"
                                                step="0.001"
                                                value={futuresData.sellPrice}
                                                onChange={(e) =>
                                                    setFuturesData({
                                                        ...futuresData,
                                                        sellPrice: handlePositiveNumber(e.target.value, 3)
                                                    })
                                                }
                                            />

                                        </Form.Group>

                                        <Form.Group className="mb-3">
                                            <Form.Label className="fw-semibold">Quantity (Lots)</Form.Label>
                                            <Form.Control
                                                type="number"
                                                min="0"
                                                step="0.001"
                                                value={futuresData.quantity}
                                                onChange={(e) =>
                                                    setFuturesData({
                                                        ...futuresData,
                                                        quantity: handlePositiveNumber(e.target.value, 3)
                                                    })
                                                }
                                            />

                                        </Form.Group>

                                        <div className="alert alert-primary mt-4">
                                            <div className="d-flex justify-content-between">
                                                <span> Selected Service:</span>
                                                <strong>{futuresData.service || 'None'}</strong>
                                            </div>
                                        </div>
                                    </>
                                ) : (
                                    // Options Form
                                    <>
                                        <Form.Group className="mb-3">
                                            <Form.Label className="fw-semibold">Select Service</Form.Label>
                                            <Form.Select
                                                value={optionsData.service}
                                                onChange={(e) => setOptionsData({ ...optionsData, service: e.target.value })}
                                                className="py-2"
                                            >
                                                <option value="">-- Select a service --</option>
                                                {availableServices.map(service => (
                                                    <option key={service} value={service}>
                                                        {service}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>

                                        <Form.Group className="mb-3">
                                            <Form.Label className="fw-semibold">
                                                Entry Price
                                                <span className="badge bg-primary ms-2">
                                                    Live: {getCurrencySymbol(optionsData.service)}{prices[optionsData.service]?.spot_price || '--'}
                                                </span>
                                            </Form.Label>
                                            <Form.Control
                                                type="number"
                                                step="0.01"
                                                placeholder="Live price will appear here"
                                                value={optionsData.buyPrice}
                                                onChange={(e) => setOptionsData({ ...optionsData, buyPrice: e.target.value })}
                                                className="py-2"
                                                disabled={!optionsData.service}
                                            />
                                        </Form.Group>

                                        <Form.Group className="mb-3">
                                            <Form.Label className="fw-semibold">Exit Price</Form.Label>
                                            <Form.Control
                                                type="number"
                                                step="0.01"
                                                placeholder="Enter sell price"
                                                value={optionsData.sellPrice}
                                                onChange={(e) => setOptionsData({ ...optionsData, sellPrice: e.target.value })}
                                                className="py-2"
                                            />
                                        </Form.Group>

                                        <Form.Group className="mb-3">
                                            <Form.Label className="fw-semibold">Number of Lots</Form.Label>
                                            <Form.Control
                                                type="number"
                                                step="1"
                                                placeholder="Enter number of lots"
                                                value={optionsData.quantity}
                                                onChange={(e) => setOptionsData({ ...optionsData, quantity: e.target.value })}
                                                className="py-2"
                                            />
                                        </Form.Group>

                                        <Form.Group className="mb-3">
                                            <Form.Label className="fw-semibold">Lot Size</Form.Label>
                                            <Form.Control
                                                type="number"
                                                step="1"
                                                placeholder="Enter lot size"
                                                value={optionsData.lotSize}
                                                onChange={(e) => setOptionsData({ ...optionsData, lotSize: e.target.value })}
                                                className="py-2"
                                            />
                                        </Form.Group>

                                        <div className="alert alert-primary mt-4">
                                            <div className="d-flex justify-content-between mb-2">
                                                <span> Selected Service:</span>
                                                <strong>{optionsData.service || 'None'}</strong>
                                            </div>
                                            <div className="d-flex justify-content-between">
                                                <span>Total Quantity:</span>
                                                <strong>{optionsData.quantity * optionsData.lotSize}</strong>
                                            </div>
                                        </div>
                                    </>
                                )}
                            </Card.Body>
                        </Card>
                    </Col>

                    {/* Results Card */}
                    <Col lg={6}>
                        <Card className="shadow-sm h-100">
                            <Card.Header className="bg-primary text-white">
                                <h5 className="mb-0"> P&L Breakdown</h5>
                            </Card.Header>
                            <Card.Body className="p-4">
                                {/* Gross P&L */}
                                <div className={`alert ${parseFloat(currentMetrics.grossPnL) >= 0 ? 'alert-primary' : 'alert-primary'} mb-4`}>
                                    <div className="d-flex justify-content-between align-items-center">
                                        <h6 className="mb-0">Gross P&L</h6>
                                        <h4 className="mb-0 fw-bold">
                                            {parseFloat(currentMetrics.grossPnL) >= 0 ? '+' : ''}{currentCurrency}{currentMetrics.grossPnL}
                                        </h4>
                                    </div>
                                </div>

                                {/* Charges Breakdown */}
                                <Table bordered hover size="sm" className="mb-4">
                                    <tbody>
                                        <tr>
                                            <td className="text-muted">Buy Value</td>
                                            <td className="text-end fw-semibold">{currentCurrency}{currentMetrics.buyValue}</td>
                                        </tr>
                                        <tr>
                                            <td className="text-muted">Sell Value</td>
                                            <td className="text-end fw-semibold">{currentCurrency}{currentMetrics.sellValue}</td>
                                        </tr>
                                        <tr className="table-light">
                                            <td className="fw-semibold">Total Turnover</td>
                                            <td className="text-end fw-bold">{currentCurrency}{currentMetrics.turnover}</td>
                                        </tr>
                                        <tr className="table-light">
                                            <td colSpan="2" className="fw-bold">Charges Breakdown</td>
                                        </tr>
                                        <tr>
                                            <td className="text-muted ps-3">Total Brokerage (0.05%)</td>
                                            <td className="text-end text-danger">-{currentCurrency}{currentMetrics.totalBrokerage}</td>
                                        </tr>
                                        <tr>
                                            <td className="text-muted ps-3">Exchange Charges (0.002%)</td>
                                            <td className="text-end text-danger">-{currentCurrency}{currentMetrics.exchangeTxn}</td>
                                        </tr>
                                        <tr className="table-warning">
                                            <td className="fw-semibold">Total Charges</td>
                                            <td className="text-end fw-bold text-danger">-{currentCurrency}{currentMetrics.totalCharges}</td>
                                        </tr>
                                    </tbody>
                                </Table>

                                {/* Net P&L */}
                                <div className={`alert ${parseFloat(currentMetrics.netPnL) >= 0 ? 'alert-primary' : 'alert-primary'}`}>
                                    <div className="d-flex justify-content-between align-items-center">
                                        <h5 className="mb-0 fw-bold">Net P&L</h5>
                                        <h3 className="mb-0 fw-bold">
                                            {parseFloat(currentMetrics.netPnL) >= 0 ? '+' : ''}{currentCurrency}{currentMetrics.netPnL}
                                        </h3>
                                    </div>
                                </div>

                                {/* Additional Info */}
                                <div className="mt-3 p-3 bg-light rounded">
                                    <small className="text-muted">
                                        <strong>Note:</strong> Brokerage charge is {BROKERAGE_RATE}% on total turnover (buy + sell value).
                                        Exchange charges are {EXCHANGE_TXN_RATE}% on turnover.
                                    </small>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                {/* Formula Info Card */}
                {/* <Card className="mt-4 shadow-sm">
                    <Card.Header className="bg-primary text-white">
                        <h6 className="mb-0">📐 Calculation Formula</h6>
                    </Card.Header>
                    <Card.Body>
                        <Row>
                            <Col md={6}>
                                <div className="p-3 bg-light rounded mb-3">
                                    <h6 className="fw-bold text-primary">Gross P&L</h6>
                                    <code className="d-block">(Exit Price - Entry Price) × Quantity</code>
                                </div>
                            </Col>
                            <Col md={6}>
                                <div className="p-3 bg-light rounded mb-3">
                                    <h6 className="fw-bold text-primary">Net P&L</h6>
                                    <code className="d-block">Gross P&L - Total Charges</code>
                                </div>
                            </Col>
                            <Col md={12}>
                                <div className="p-3 bg-light rounded">
                                    <h6 className="fw-bold text-primary">Total Charges</h6>
                                    <code className="d-block">Brokerage (Buy + Sell) + Exchange Charges</code>
                                </div>
                            </Col>
                        </Row>
                    </Card.Body>
                </Card> */}
            </div>

            <style jsx>{`
                .calculation-container {
                    max-width: 1400px;
                    margin: 0 auto;
                }
                
                .nav-pills .nav-link {
                    border-radius: 50px;
                    transition: all 0.3s ease;
                }
                
                .nav-pills .nav-link.active {
                    transform: translateY(-2px);
                    box-shadow: 0 4px 8px rgba(0,0,0,0.1);
                }
                
                .card {
                    border: none;
                    transition: transform 0.2s ease;
                }
                
                .card:hover {
                    transform: translateY(-2px);
                }
                
                .form-control:focus {
                    border-color: #0d6efd;
                    box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25);
                }
                
                .alert {
                    border-radius: 10px;
                }
                
                code {
                    background-color: #f8f9fa;
                    padding: 8px 12px;
                    border-radius: 5px;
                    font-size: 0.9rem;
                }
            `}</style>
        </Content>
    );
}